import { FC } from 'react';
import Slider from 'rc-slider';
import { TFunction } from 'i18next';
import { Controller, UseFormSetValue } from 'react-hook-form';
import { workPreferencesOptions } from '$constants/workPreferences';
import { MultiSelect } from 'primereact/multiselect';
import ChipList from '../ChipList/ChipList';
import MultipleTagSelector from '../MultipleTagSelector/MultipleTagSelector';
import {
  SliderActiveDotStyle,
  SliderDefaultValue,
  SliderDotStyle,
  SliderHandleStyle,
  SliderHandleStyleInternal,
  SliderMax,
  SliderMin,
  SliderRailStyle,
  SliderTrackStyle,
  SliderTrackStyleInternal,
} from '../../constants/sliderStyles';
import NumberInput from '../NumberInput/NumberInput';
import classes from './JobInterestsForm.module.css';
import FormsCard from '../FormsCard/FormsCard';
import { JobPosition } from '../../types/candidates';
import { AutocompleteOption, JobInterest } from '../../types/user_info';

interface JobInterestsFormProps {
  jobPositions: JobPosition[] | undefined;
  setValue: UseFormSetValue<JobInterest>;
  selectedTags: AutocompleteOption[] | undefined;
  addToJobInterestList: (option: AutocompleteOption) => void;
  salary: number[];
  partTimeSalary: number;
  hourlyRate: number;
  t: TFunction<'global', undefined>;
  secondary?: boolean;
  control: any;
  selectedWorkPreferences: string[] | undefined;
}
const JobInterestsForm: FC<JobInterestsFormProps> = ({
  jobPositions,
  setValue,
  selectedTags,
  addToJobInterestList,
  salary,
  partTimeSalary,
  hourlyRate,
  t,
  control,
  secondary,
  selectedWorkPreferences,
}) => {
  const trackStyle = !secondary
    ? [SliderTrackStyle]
    : [SliderTrackStyleInternal];

  const handleStyle = !secondary
    ? [SliderHandleStyle, SliderHandleStyle]
    : [SliderHandleStyleInternal, SliderHandleStyleInternal];

  const multiSelectClass = secondary
    ? classes.multiSelectInternal
    : classes.multiSelect;

  const selectedItemClass = secondary
    ? classes.multiSelectSelectedOptionInternal
    : classes.multiSelectSelectedOption;

  const checkboxClass = secondary
    ? classes.multiSelectSelectedCheckboxInternal
    : classes.multiSelectSelectedCheckbox;

  const chipClass = secondary
    ? classes.multiSelectChipInternal
    : classes.multiSelectChip;

  return (
    <FormsCard>
      <MultipleTagSelector
        arrayName="JobInterest"
        label={t('jobs.jobTitle')}
        options={jobPositions ?? []}
        setValue={setValue}
        selectedTags={selectedTags ?? []}
        secondary={secondary}
      />
      <ChipList
        defaultTags={jobPositions ? jobPositions.slice(0, 7) : []}
        onClickAction={addToJobInterestList}
        selectedTags={selectedTags ?? []}
        secondary={secondary}
      />
      <div>
        <p className={classes.SalaryLabel}>{t('jobs.salary')}</p>
        <div className={classes.salaryButtonsContainer}>
          <NumberInput
            label={t('jobs.salaryMin')}
            value={`$${salary?.[0]?.toLocaleString().replace(',', '.')}`}
            separator
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              if (Number(value) < SliderMax) {
                setValue('Salary', [Number(value), salary?.[1]], {
                  shouldDirty: true,
                });
              }
            }}
          />

          <NumberInput
            label={t('jobs.salaryMax')}
            value={`$${salary?.[1]?.toLocaleString().replace(',', '.')}`}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              if (Number(value) <= SliderMax)
                setValue('Salary', [salary?.[0], Number(value)], {
                  shouldDirty: true,
                });
            }}
          />
        </div>

        <div className={classes.SalaryContainer}>
          <Controller
            name="Salary"
            control={control}
            defaultValue={SliderDefaultValue}
            rules={{
              validate: {
                salaryMin: (value) =>
                  value[0] < value[1] || `${t('jobs.errorSalary')}`,
              },
            }}
            render={({ field, formState: { errors } }) => {
              return (
                <div className={classes.rangeContainer}>
                  {errors?.Salary && (
                    <p className={classes.errorText}>
                      {(errors?.Salary as any)?.message}
                    </p>
                  )}
                  <div className={classes.rangeContainerSlider}>
                    <p className={classes.leftSalary}>$1</p>
                    <div style={{ width: '100%' }}>
                      <Slider
                        range
                        min={SliderMin}
                        max={SliderMax}
                        className={classes.SalarySlider}
                        allowCross
                        step={50}
                        defaultValue={SliderDefaultValue}
                        railStyle={SliderRailStyle}
                        dotStyle={SliderDotStyle}
                        activeDotStyle={SliderActiveDotStyle}
                        handleStyle={handleStyle}
                        trackStyle={trackStyle}
                        onChange={field.onChange}
                        ref={field.ref}
                        value={field.value.map((e: any) => Number(e))}
                      />
                    </div>
                    <p className={`${classes.rightSalary} text-nowrap`}>
                      +$100K
                    </p>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
      <div className={classes.salaryButtonsContainer}>
        <NumberInput
          label={t('jobs.partTimeSalary')}
          value={`$${partTimeSalary}`}
          separator
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            setValue('PartTimeRate', Number(value), {
              shouldDirty: selectedTags ? selectedTags?.length > 0 : false,
            });
          }}
        />
        <NumberInput
          label={t('jobs.hourlyRate')}
          value={`$${hourlyRate}`}
          separator
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            setValue('HourlyRate', Number(value), {
              shouldDirty: selectedTags ? selectedTags?.length > 0 : false,
            });
          }}
        />

        <Controller
          name="WorkPreference"
          control={control}
          render={({ field }) => (
            <div className={classes.multiSelectContainer}>
              <p data-tesid="label" className={classes.multiSelectLabel}>
                {t('jobs.workPreference')}
              </p>
              <MultiSelect
                className={multiSelectClass}
                value={selectedWorkPreferences || []}
                options={workPreferencesOptions.map((opt) => ({
                  label: opt,
                  value: opt,
                }))}
                onChange={(e) => field.onChange(e.value)}
                display="chip"
                pt={{
                  token: {
                    className: chipClass,
                  },
                  item: ({ context }: { context: { selected: boolean } }) => ({
                    className: context?.selected && selectedItemClass,
                  }),
                  checkbox: {
                    box: ({ context }: { context: { checked: boolean } }) => ({
                      className: context?.checked && checkboxClass,
                    }),
                  },
                  headerCheckbox: {
                    box: ({ context }: { context: { checked: boolean } }) => ({
                      className: context?.checked && checkboxClass,
                    }),
                  },
                }}
              />
            </div>
          )}
        />
      </div>
    </FormsCard>
  );
};
JobInterestsForm.defaultProps = {
  secondary: false,
};
export default JobInterestsForm;
